import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  CreateOpContractSchema,
  fmt,
  fmtB,
  fmtDate,
  type CreateOpContract,
  type OpContractAccrual,
  type OpContractAccrualRun,
  type OpContractDisclosure,
  type OpContractExemptionBasis,
  type OpContractListRow,
  type PeriodMaster,
  type Vendor,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';

import {
  addMonthsLastDay,
  buildAccrualStatusMap,
  buildCreateContractPayload,
  cellStatus,
  contractDrawdown,
  EXEMPTION_BASES,
  formatPeriodLabel,
  groupAccrualsByPeriod,
  needsReclassification,
  noticeInfo,
  pickDefaultPeriod,
  vendorOptions,
} from './opcontract.logic';
import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { openPeriods } from '../components/journal/periodGuard';
import { PageHead } from '../components/layout';
import { OpContractApprovalPanel } from '../components/op-contract/OpContractApprovalPanel';
import { OpContractRunApprovalPanel } from '../components/op-contract/OpContractRunApprovalPanel';
import { SearchSelect } from '../components/SearchSelect';
import {
  SpreadsheetImportModal,
  type ImportFieldSpec,
  type ImportResult,
  type ParsedUnit,
} from '../components/SpreadsheetImportModal';
import {
  useAccrualRuns,
  useAccrueOpContracts,
  useActiveCompany,
  useActiveCompanyId,
  useAddOpContractRate,
  useBranches,
  useCoa,
  useCreateOpContract,
  useDeleteOpContractRate,
  useDepartments,
  useOpContract,
  useOpContractAccruals,
  useOpContractDisclosure,
  useOpContractRates,
  useOpContracts,
  usePeriods,
  useRenewOpContract,
  useReverseOpContracts,
  useVendors,
} from '../lib/queries';
import { exportRows, headerIndexer, parseBool, todayStamp, type ExportColumn } from '../lib/xlsx';

type TabKey = 'register' | 'schedule' | 'run' | 'renewal' | 'disclosure';
type StatusKey = 'active' | 'expiring' | 'expired' | 'pending';
type CatCode =
  | 'COPIER'
  | 'SECURITY'
  | 'CLEAN'
  | 'WATER'
  | 'PEST'
  | 'GARDEN'
  | 'IT_SUPP'
  | 'LIFT'
  | 'AC'
  | 'INT_TEL';

interface OCCat {
  code: CatCode;
  label: string;
  acct: string;
  tone: string;
}

interface OCInput {
  id: string;
  vendor: string;
  cat: CatCode;
  desc: string;
  start: string;
  end: string;
  months: number;
  monthly: number;
  branch: string;
  paymentTerms: string;
  payRef: string;
  renewable: boolean;
  active: number;
  total: number;
  status: StatusKey;
}

interface OCContract extends OCInput {
  apiId?: string;
  vendorId?: string;
  daysLeft: number;
  totalValue: number;
  remainValue: number;
  noticePeriodDays?: number | null;
  autoRenew?: boolean;
  renewedToCode?: string | null;
  // M3 maker-checker engine state (Decision-Z). null/undefined for legacy rows.
  approvalStatus?: string | null;
  pendingAction?: string | null;
  // M4 TFRS 16 — exemption basis (null/undefined = unclassified) + the
  // server-derived reclassification flag (SHORT_TERM with a term > 12 months).
  exemptionBasis?: OpContractExemptionBasis | null;
  needsReclassification?: boolean;
  // M5a escalation — the rate effective for the CURRENT period from the
  // contract's rate schedule (falls back to the base monthly when no override
  // applies). The register shows this so the list reflects escalation.
  currentRate?: number;
}

interface Totals {
  contractValue: number;
  monthlyRun: number;
  expiringIn90: number;
  expiringAmt: number;
  activeCount: number;
  pendingCount: number;
}

// Per-category default expense account — real 8-digit COA codes (verified present
// in coa-master.json). These are convenience DEFAULTS only: the create form lets
// the user pick any postable EXPENSE/COST account, and both create + import
// validate the code against the company COA server-side. Categories without a
// purpose-built COA line fall to the generic 60110019 ค่าจ้างบริการอื่น.
const OC_CATS: readonly OCCat[] = [
  { code: 'COPIER', label: 'เครื่องถ่ายเอกสาร / เครื่องพิมพ์', acct: '60110019', tone: '#1a56a6' },
  { code: 'SECURITY', label: 'รักษาความปลอดภัย (รปภ.)', acct: '60110040', tone: '#7030A0' },
  { code: 'CLEAN', label: 'ทำความสะอาด', acct: '60110041', tone: '#117a3d' },
  { code: 'WATER', label: 'น้ำดื่ม / กรองน้ำ', acct: '60110019', tone: '#0e7490' },
  { code: 'PEST', label: 'กำจัดปลวก / แมลง', acct: '60110019', tone: '#b54708' },
  { code: 'GARDEN', label: 'ดูแลสวน / ภูมิทัศน์', acct: '60110019', tone: '#65a30d' },
  { code: 'IT_SUPP', label: 'IT Support / Maintenance', acct: '60130003', tone: '#a16207' },
  { code: 'LIFT', label: 'บำรุงรักษาลิฟต์', acct: '60110037', tone: '#52525b' },
  { code: 'AC', label: 'บำรุงรักษาแอร์ / Chiller', acct: '60110037', tone: '#b42318' },
  { code: 'INT_TEL', label: 'Internet / โทรศัพท์ Leased Line', acct: '60110034', tone: '#0369a1' },
];

const OC_FALLBACK: OCCat = OC_CATS[0]!;

function getOCCat(c: CatCode): OCCat {
  return OC_CATS.find((x) => x.code === c) ?? OC_FALLBACK;
}

// contractType -> default expense account code, used to derive expenseAccountCode
// on import when the column is left blank.
const OC_ACCT_BY_TYPE = new Map<string, string>(OC_CATS.map((c) => [c.code, c.acct]));

// ---- Import field reference (rendered in the pick stage of the modal) ----
const OC_IMPORT_FIELDS: readonly ImportFieldSpec[] = [
  {
    col: 'contractType',
    label: 'ประเภทสัญญา',
    required: true,
    hint: 'COPIER, SECURITY, CLEAN, WATER, PEST, GARDEN, IT_SUPP, LIFT, AC, INT_TEL',
  },
  { col: 'vendorCode', label: 'รหัสคู่สัญญา', required: false, hint: 'รหัส Vendor (ถ้ามี)' },
  { col: 'startDate', label: 'วันเริ่ม', required: true, hint: 'YYYY-MM-DD' },
  { col: 'endDate', label: 'วันสิ้นสุด', required: true, hint: 'YYYY-MM-DD · ต้องหลังวันเริ่ม' },
  { col: 'monthlyAmount', label: 'ค่าใช้จ่าย/เดือน', required: true, hint: 'ตัวเลข > 0' },
  {
    col: 'expenseAccountCode',
    label: 'รหัสบัญชีค่าใช้จ่าย',
    required: false,
    hint: 'ถ้าเว้นว่าง จะอนุมานจากประเภทสัญญา',
  },
  { col: 'branchCode', label: 'รหัสสาขา', required: false, hint: 'รหัสสาขา (ถ้ามี)' },
  {
    col: 'departmentCode',
    label: 'รหัสหน่วยงาน',
    required: false,
    hint: 'รหัส Cost Center (ถ้ามี)',
  },
  {
    col: 'autoAccrual',
    label: 'รับรู้อัตโนมัติ',
    required: false,
    hint: 'true/false · ค่าเริ่มต้น true',
  },
  { col: 'notes', label: 'หมายเหตุ', required: false, hint: '' },
];

const OC_IMPORT_TEMPLATE_COLUMNS: readonly string[] = [
  'contractType',
  'vendorCode',
  'startDate',
  'endDate',
  'monthlyAmount',
  'expenseAccountCode',
  'branchCode',
  'departmentCode',
  'autoAccrual',
  'notes',
];

const MONTHS = [
  'ม.ค.',
  'ก.พ.',
  'มี.ค.',
  'เม.ย.',
  'พ.ค.',
  'มิ.ย.',
  'ก.ค.',
  'ส.ค.',
  'ก.ย.',
  'ต.ค.',
  'พ.ย.',
  'ธ.ค.',
] as const;

const FIRST_CAT: OCCat = OC_CATS[2]!; // CLEAN as default

// Single display "today" clock for the page (M1 used this in rowsFromApi for
// daysLeft; the renewal tab reuses it so notice/cancel-by math is consistent).
const OC_TODAY = '2026-05-25';

export const Route = createFileRoute('/opcontract')({
  component: OpContractPage,
});

// Map API row → prototype OCContract shape so the existing DOM renders unchanged.
// Returns [] when the API returns no rows so the table renders an empty-state
// placeholder rather than mock data.
function rowsFromApi(rows: readonly OpContractListRow[]): readonly OCContract[] {
  const today = new Date(OC_TODAY);
  return rows.map((r) => {
    const start = new Date(r.startDate);
    const end = new Date(r.endDate);
    const months = Math.max(
      1,
      (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1,
    );
    const monthly = Number(r.monthlyAmount);
    const daysLeft = Math.floor((end.getTime() - today.getTime()) / 86_400_000);
    let status: StatusKey = 'active';
    if (r.status === 'TERMINATED') status = 'expired';
    else if (daysLeft <= 0) status = 'expired';
    else if (daysLeft <= 90) status = 'expiring';
    if (start.getTime() > today.getTime()) status = 'pending';
    const totalValue = monthly * months;
    const active = r.monthsAccrued;
    return {
      id: r.code,
      apiId: r.id,
      ...(r.vendorId ? { vendorId: r.vendorId } : {}),
      vendor: r.vendorName ?? '—',
      cat: (r.contractType as CatCode) ?? 'CLEAN',
      desc: r.notes ?? '',
      start: start.toISOString().slice(0, 10),
      end: end.toISOString().slice(0, 10),
      months,
      monthly,
      branch: r.branchName ?? '—',
      paymentTerms: 'รายเดือน · Net 30',
      payRef: '—',
      renewable: true,
      active,
      total: months,
      status,
      daysLeft,
      totalValue,
      remainValue: monthly * (months - active),
      noticePeriodDays: r.noticePeriodDays ?? null,
      autoRenew: r.autoRenew,
      renewedToCode: r.renewedToCode ?? null,
      approvalStatus: r.approvalStatus ?? null,
      pendingAction: r.pendingAction ?? null,
      exemptionBasis: r.exemptionBasis ?? null,
      // Prefer the server-derived flag; fall back to the pure helper so a row
      // without it (legacy/partial payload) still flags correctly off months.
      needsReclassification:
        r.needsReclassification ?? needsReclassification(r.exemptionBasis, r.monthsTotal),
      // M5a — the period-effective rate from the escalation schedule (absent on
      // legacy rows → the register then shows the base monthly unchanged).
      ...(r.currentRate != null ? { currentRate: Number(r.currentRate) } : {}),
    } satisfies OCContract;
  });
}

export function OpContractPage() {
  const companyId = useActiveCompanyId();
  const company = useActiveCompany();
  const contractsQuery = useOpContracts(companyId);

  const { data: vendors = [] } = useVendors(companyId);
  const { data: branches = [] } = useBranches(companyId);
  const { data: departments = [] } = useDepartments(companyId);
  const { data: accruals = [] } = useOpContractAccruals(companyId);
  const { data: periods = [] } = usePeriods(companyId);
  const createContract = useCreateOpContract(companyId);

  const [tab, setTab] = useState<TabKey>('register');
  const [showNew, setShowNew] = useState<boolean>(false);
  const [showRun, setShowRun] = useState<boolean>(false);
  const [showRenew, setShowRenew] = useState<OCContract | null>(null);
  const [showImport, setShowImport] = useState<boolean>(false);
  const [q, setQ] = useState<string>('');
  const [catF, setCatF] = useState<string>('all');
  const [statusF, setStatusF] = useState<string>('all');
  const [year, setYear] = useState<number>(2026);
  const [showDetail, setShowDetail] = useState<string | null>(null);
  const [reversePeriod, setReversePeriod] = useState<string | null>(null);
  const [vendorF, setVendorF] = useState<string>('all');

  // code <-> id maps for import resolution / export rendering.
  const vendorByCode = useMemo(
    () => new Map(vendors.map((v) => [v.code.toLowerCase(), v.id])),
    [vendors],
  );
  const vendorCodeById = useMemo(() => new Map(vendors.map((v) => [v.id, v.code])), [vendors]);
  const branchByCode = useMemo(
    () => new Map(branches.map((b) => [b.code.toLowerCase(), b.id])),
    [branches],
  );
  const branchCodeById = useMemo(() => new Map(branches.map((b) => [b.id, b.code])), [branches]);
  const deptByCode = useMemo(
    () => new Map(departments.map((d) => [d.code.toLowerCase(), d.id])),
    [departments],
  );

  const apiRows: readonly OpContractListRow[] = contractsQuery.data ?? [];

  const EXPORT_COLS: ExportColumn<OpContractListRow>[] = [
    { header: 'code', value: (r) => r.code, width: 18 },
    { header: 'contractType', value: (r) => r.contractType, width: 12 },
    {
      header: 'vendorCode',
      value: (r) => (r.vendorId ? (vendorCodeById.get(r.vendorId) ?? '') : ''),
      width: 14,
    },
    { header: 'startDate', value: (r) => String(r.startDate).slice(0, 10), width: 12 },
    { header: 'endDate', value: (r) => String(r.endDate).slice(0, 10), width: 12 },
    { header: 'monthlyAmount', value: (r) => Number(r.monthlyAmount), width: 14 },
    { header: 'expenseAccountCode', value: (r) => r.expenseAccountCode, width: 16 },
    {
      header: 'branchCode',
      value: (r) => (r.branchId ? (branchCodeById.get(r.branchId) ?? '') : ''),
      width: 12,
    },
    { header: 'monthsTotal', value: (r) => r.monthsTotal, width: 12 },
    { header: 'accruedAmount', value: (r) => Number(r.accruedAmount), width: 14 },
    { header: 'status', value: (r) => r.status, width: 12 },
  ];

  async function handleExport() {
    const filename =
      'สัญญาดำเนินงาน_' + (company?.code ?? 'COMPANY') + '_' + todayStamp() + '.xlsx';
    await exportRows(filename, 'สัญญาดำเนินงาน', EXPORT_COLS, apiRows);
  }

  function parse(grid: string[][]): {
    units: ParsedUnit<CreateOpContract>[];
    headerError: string | null;
  } {
    const header = grid[0];
    if (!header) return { units: [], headerError: 'ไฟล์ว่างเปล่า' };
    const { idx, cell } = headerIndexer(header);
    for (const req of ['contractType', 'startDate', 'endDate', 'monthlyAmount']) {
      if (idx(req) === -1) return { units: [], headerError: 'ไม่พบคอลัมน์ที่จำเป็น: ' + req };
    }
    const units: ParsedUnit<CreateOpContract>[] = [];
    for (let r = 1; r < grid.length; r++) {
      const cols = grid[r];
      if (!cols || cols.every((c) => c.trim() === '')) continue;
      const rowNo = r + 1;

      const vendorCode = cell(cols, 'vendorCode');
      let vendorId: string | null = null;
      if (vendorCode) {
        vendorId = vendorByCode.get(vendorCode.toLowerCase()) ?? null;
        if (!vendorId) {
          units.push({ rowNo, data: null, error: 'ไม่พบคู่สัญญา: ' + vendorCode });
          continue;
        }
      }

      const branchCode = cell(cols, 'branchCode');
      let branchId: string | null = null;
      if (branchCode) {
        branchId = branchByCode.get(branchCode.toLowerCase()) ?? null;
        if (!branchId) {
          units.push({ rowNo, data: null, error: 'ไม่พบสาขา: ' + branchCode });
          continue;
        }
      }

      const departmentCode = cell(cols, 'departmentCode');
      let departmentId: string | null = null;
      if (departmentCode) {
        departmentId = deptByCode.get(departmentCode.toLowerCase()) ?? null;
        if (!departmentId) {
          units.push({ rowNo, data: null, error: 'ไม่พบหน่วยงาน: ' + departmentCode });
          continue;
        }
      }

      const contractType = cell(cols, 'contractType');
      const expenseAccountCode =
        cell(cols, 'expenseAccountCode') || (OC_ACCT_BY_TYPE.get(contractType) ?? '');

      const candidate = {
        contractType,
        vendorId,
        startDate: cell(cols, 'startDate'),
        endDate: cell(cols, 'endDate'),
        monthlyAmount: Number(cell(cols, 'monthlyAmount')),
        expenseAccountCode,
        branchId,
        departmentId,
        autoAccrual: parseBool(cell(cols, 'autoAccrual'), true),
        notes: cell(cols, 'notes') || undefined,
      };
      const p = CreateOpContractSchema.safeParse(candidate);
      units.push(
        p.success
          ? { rowNo, data: p.data, error: null }
          : { rowNo, data: null, error: p.error.issues[0]?.message ?? 'ข้อมูลไม่ถูกต้อง' },
      );
    }
    return { units, headerError: null };
  }

  async function runImport(
    valid: ParsedUnit<CreateOpContract>[],
    onProgress: (done: number, total: number) => void,
  ): Promise<ImportResult> {
    const res: ImportResult = { created: 0, updated: 0, failed: 0, errors: [] };
    for (let i = 0; i < valid.length; i++) {
      const u = valid[i];
      if (!u?.data) continue;
      try {
        await createContract.mutateAsync(u.data);
        res.created++;
      } catch (e) {
        res.failed++;
        res.errors.push({
          code: 'แถว ' + u.rowNo,
          message: e instanceof Error ? e.message : 'เกิดข้อผิดพลาด',
        });
      }
      onProgress(i + 1, valid.length);
    }
    return res;
  }

  const rows: readonly OCContract[] = useMemo(
    () => rowsFromApi(contractsQuery.data ?? []),
    [contractsQuery.data],
  );

  const filtered = useMemo<readonly OCContract[]>(
    () =>
      rows.filter((c) => {
        if (catF !== 'all' && c.cat !== catF) return false;
        if (statusF !== 'all' && c.status !== statusF) return false;
        if (vendorF !== 'all' && c.vendorId !== vendorF) return false;
        if (
          q &&
          !(
            c.id.toLowerCase().includes(q.toLowerCase()) ||
            c.vendor.toLowerCase().includes(q.toLowerCase()) ||
            c.desc.toLowerCase().includes(q.toLowerCase())
          )
        )
          return false;
        return true;
      }),
    [rows, q, catF, statusF, vendorF],
  );

  const totals = useMemo<Totals>(() => {
    const act = rows.filter((c) => c.status === 'active' || c.status === 'expiring');
    return {
      contractValue: act.reduce((s, c) => s + c.totalValue, 0),
      monthlyRun: act.reduce((s, c) => s + c.monthly, 0),
      expiringIn90: rows.filter((c) => c.status === 'expiring').length,
      expiringAmt: rows.filter((c) => c.status === 'expiring').reduce((s, c) => s + c.monthly, 0),
      activeCount: act.length,
      pendingCount: rows.filter((c) => c.status === 'pending').length,
    };
  }, [rows]);

  const tabs: readonly (readonly [TabKey, string])[] = [
    ['register', 'ทะเบียนสัญญา'],
    ['schedule', 'Recognition Schedule (Matrix)'],
    ['run', 'ประวัติการรับรู้รายเดือน'],
    ['renewal', 'สัญญาใกล้หมดอายุ / Renewal'],
    ['disclosure', 'เปิดเผย TFRS 16'],
  ];

  return (
    <div className="page">
      <PageHead
        crumbs={['บัญชีแยกประเภท', 'สัญญาดำเนินงาน']}
        title="สัญญาดำเนินงาน (Operating Service Contracts) — ไม่เข้า TFRS 16"
        actions={
          <>
            <HelpButton guide="opcontract" />
            <select
              className="select"
              value={year}
              onChange={(e) => setYear(parseInt(e.target.value, 10))}
            >
              {Array.from({ length: 4 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y}>
                  ปี: {y}
                </option>
              ))}
            </select>
            <button className="btn" onClick={() => setShowRun(true)}>
              <Icon name="refresh" size={14} /> รับรู้รายเดือน
            </button>
            <button className="btn" onClick={() => setShowImport(true)} disabled={!companyId}>
              <Icon name="upload" size={14} /> นำเข้า
            </button>
            <button
              className="btn"
              onClick={() => void handleExport()}
              disabled={apiRows.length === 0}
            >
              <Icon name="download" size={14} /> ส่งออก
            </button>
            <button className="btn primary" onClick={() => setShowNew(true)}>
              <Icon name="plus" size={14} /> เพิ่มสัญญาใหม่
            </button>
          </>
        }
      />

      <div className="kpi-grid">
        <div className="kpi">
          <div className="label">
            <span className="dot" />
            มูลค่าสัญญารวม (Active)
          </div>
          <div className="value tnum">
            {fmtB(totals.contractValue)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">{totals.activeCount} สัญญา · ตลอดอายุสัญญา</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--brand)' }} />
            ค่าใช้จ่ายต่อเดือน
          </div>
          <div className="value tnum">
            {fmt(totals.monthlyRun)} <span className="unit">บาท</span>
          </div>
          <div className="trend faint">Monthly Recognition Total</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            ใกล้หมดอายุ ≤90 วัน
          </div>
          <div
            className="value tnum"
            style={{ color: totals.expiringIn90 ? 'var(--warning)' : 'inherit' }}
          >
            {totals.expiringIn90} <span className="unit">สัญญา</span>
          </div>
          <div className="trend down">รวม/เดือน {fmt(totals.expiringAmt)} บาท</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            สัญญาใหม่รอเริ่ม
          </div>
          <div className="value tnum">
            {totals.pendingCount} <span className="unit">สัญญา</span>
          </div>
          <div className="trend faint">ลงทะเบียนแล้ว · รอเริ่มงวด</div>
        </div>
      </div>

      <div
        style={{
          background: '#fafafa',
          border: '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 18px',
          marginBottom: 'var(--section-gap)',
        }}
      >
        <div className="section-title" style={{ margin: '0 0 10px' }}>
          หลักการ — ทำไมไม่เข้า TFRS 16
        </div>
        <div
          style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 12, fontSize: 14 }}
        >
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#1a56a6',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              S
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Service Contract</div>
              <div className="meta" style={{ marginTop: 2 }}>
                เป็นการให้บริการ ไม่มี Right of Use · เช่น รปภ., ทำความสะอาด, IT Support
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#117a3d',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              L
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Low-value Asset</div>
              <div className="meta" style={{ marginTop: 2 }}>
                มูลค่าใหม่ &lt; 5,000 USD · เช่น เครื่องถ่ายเอกสาร · ใช้ข้อยกเว้น TFRS 16.5
              </div>
            </div>
          </div>
          <div
            style={{
              display: 'flex',
              gap: 10,
              padding: '10px',
              background: 'white',
              borderRadius: 6,
              border: '1px solid var(--border)',
            }}
          >
            <div
              style={{
                width: 28,
                height: 28,
                borderRadius: '50%',
                background: '#b54708',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontWeight: 700,
                fontFamily: 'var(--font-display)',
                flexShrink: 0,
              }}
            >
              T
            </div>
            <div>
              <div style={{ fontWeight: 600 }}>Short-term ≤12 เดือน</div>
              <div className="meta" style={{ marginTop: 2 }}>
                สัญญาสั้น · ใช้ข้อยกเว้น TFRS 16.5 · ลงเป็นค่าใช้จ่ายตรงๆ
              </div>
            </div>
          </div>
        </div>
        <div
          style={{
            marginTop: 10,
            padding: '10px 14px',
            background: 'white',
            borderRadius: 6,
            border: '1px solid var(--border)',
            fontSize: 14,
          }}
        >
          <b>การลงบัญชี:</b>{' '}
          <span className="code muted">
            Dr. ค่าใช้จ่าย (6xxx) / Cr. ค่าใช้จ่ายค้างจ่าย (20170001) หรือ เงินสด (10110002)
          </span>{' '}
          — ทุกงวดเท่าๆ กัน ไม่มี ROU Asset / ไม่มี Lease Liability
        </div>
      </div>

      <div className="page-tabs">
        {tabs.map(([k, l]) => (
          <div key={k} className={`pt ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>
            {l}
          </div>
        ))}
      </div>

      {tab === 'register' && (
        <OCRegister
          rows={filtered}
          q={q}
          setQ={setQ}
          catF={catF}
          setCatF={setCatF}
          statusF={statusF}
          setStatusF={setStatusF}
          vendorF={vendorF}
          setVendorF={setVendorF}
          vendors={vendors}
          onRenew={setShowRenew}
          onView={(c) => c.apiId && setShowDetail(c.apiId)}
          isPending={contractsQuery.isPending}
          error={contractsQuery.error}
        />
      )}
      {tab === 'schedule' && (
        <OCSchedule rows={rows} accruals={accruals} periods={periods} year={year} />
      )}
      {tab === 'run' && (
        <OCRunHistory
          companyId={companyId}
          accruals={accruals}
          periods={periods}
          onRun={() => setShowRun(true)}
          onReverse={(p) => setReversePeriod(p)}
        />
      )}
      {tab === 'renewal' && <OCRenewal rows={rows} onRenew={setShowRenew} />}
      {tab === 'disclosure' && <OCDisclosure companyId={companyId} year={year} />}

      {showNew && <NewContractModal companyId={companyId} onClose={() => setShowNew(false)} />}
      {showRun && (
        <RunModal
          companyId={companyId}
          rows={rows}
          periods={periods}
          onClose={() => setShowRun(false)}
        />
      )}
      {showRenew && (
        <RenewModal companyId={companyId} contract={showRenew} onClose={() => setShowRenew(null)} />
      )}
      {showDetail && (
        <ContractDetailModal
          companyId={companyId}
          contractId={showDetail}
          onClose={() => setShowDetail(null)}
          onReverse={(p) => setReversePeriod(p)}
        />
      )}
      {reversePeriod && (
        <ReverseConfirmModal
          companyId={companyId}
          period={reversePeriod}
          periods={periods}
          onClose={() => setReversePeriod(null)}
        />
      )}
      {showImport && companyId && (
        <SpreadsheetImportModal<CreateOpContract>
          title="นำเข้าสัญญาดำเนินงาน"
          intro="นำเข้าสัญญาดำเนินงานจากไฟล์ Excel — หนึ่งแถวต่อหนึ่งสัญญา"
          fields={OC_IMPORT_FIELDS as ImportFieldSpec[]}
          templateColumns={OC_IMPORT_TEMPLATE_COLUMNS as string[]}
          templateExamples={[
            [
              'SECURITY',
              '',
              '2026-06-01',
              '2027-05-31',
              50000,
              '60110040',
              '',
              '',
              'true',
              'รปภ. 24 ชม.',
            ],
          ]}
          templateFilename="op_contract_template.xlsx"
          sheetName="สัญญาดำเนินงาน"
          parse={parse}
          previewColumns={[
            { header: 'ประเภท', width: 100, render: (d) => d.contractType },
            { header: 'เริ่ม', width: 100, render: (d) => d.startDate },
            { header: 'สิ้นสุด', width: 100, render: (d) => d.endDate },
            { header: 'ค่า/เดือน', width: 100, render: (d) => fmt(d.monthlyAmount) },
            { header: 'บัญชี', width: 80, render: (d) => d.expenseAccountCode },
          ]}
          runImport={runImport}
          onClose={() => setShowImport(false)}
        />
      )}
    </div>
  );
}

interface RegisterProps {
  rows: readonly OCContract[];
  q: string;
  setQ: (s: string) => void;
  catF: string;
  setCatF: (s: string) => void;
  statusF: string;
  setStatusF: (s: string) => void;
  vendorF: string;
  setVendorF: (s: string) => void;
  vendors: Vendor[];
  onRenew: (c: OCContract) => void;
  onView: (c: OCContract) => void;
  isPending: boolean;
  error: Error | null;
}

function OCRegister({
  rows,
  q,
  setQ,
  catF,
  setCatF,
  statusF,
  setStatusF,
  vendorF,
  setVendorF,
  vendors,
  onRenew,
  onView,
  isPending,
  error,
}: RegisterProps) {
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <input
          className="input search w-lg"
          placeholder="ค้นหา เลขที่ / Vendor / รายละเอียด"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")",
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" value={catF} onChange={(e) => setCatF(e.target.value)}>
          <option value="all">ประเภท: ทั้งหมด</option>
          {OC_CATS.map((c) => (
            <option key={c.code} value={c.code}>
              {c.label}
            </option>
          ))}
        </select>
        <select className="select" value={statusF} onChange={(e) => setStatusF(e.target.value)}>
          <option value="all">สถานะ: ทั้งหมด</option>
          <option value="active">กำลังใช้งาน</option>
          <option value="expiring">ใกล้หมดอายุ ≤90 วัน</option>
          <option value="expired">หมดอายุ</option>
          <option value="pending">รอเริ่มต้น</option>
        </select>
        <select className="select" value={vendorF} onChange={(e) => setVendorF(e.target.value)}>
          <option value="all">คู่สัญญา: ทั้งหมด</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.nameTh}
            </option>
          ))}
        </select>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เลขที่</th>
              <th>Vendor / รายละเอียด</th>
              <th>ประเภท</th>
              <th>สาขา</th>
              <th>เริ่ม → สิ้นสุด</th>
              <th className="num">งวด</th>
              <th className="num">ค่าใช้จ่าย/เดือน</th>
              <th className="num">มูลค่าสัญญา</th>
              <th style={{ width: 160 }}>ความคืบหน้า</th>
              <th className="num">เหลือ (วัน)</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : error ? (
              <tr>
                <td
                  colSpan={12}
                  className="muted"
                  style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
                >
                  โหลดล้มเหลว: {error.message}
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={12} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีข้อมูล — กดปุ่ม &quot;<b>+ เพิ่มสัญญาใหม่</b>&quot; เพื่อสร้างรายการแรก
                </td>
              </tr>
            ) : null}
            {rows.map((c) => {
              const cat = getOCCat(c.cat);
              const pct = Math.round((c.active / c.total) * 100);
              return (
                <tr key={c.id}>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{c.id}</a>
                  </td>
                  <td>
                    <b>{c.vendor}</b>
                    <div className="meta">{c.desc}</div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{ width: 9, height: 9, borderRadius: 2, background: cat.tone }}
                      />
                      {cat.label}
                    </span>
                  </td>
                  <td className="muted">{c.branch}</td>
                  <td className="muted">
                    {fmtDate(c.start)}
                    <div className="meta">{fmtDate(c.end)}</div>
                  </td>
                  <td className="num">
                    {c.active}/{c.total}
                  </td>
                  <td className="num tnum">
                    {fmt(c.currentRate ?? c.monthly)}
                    {c.currentRate != null && c.currentRate !== c.monthly ? (
                      <span
                        className="meta"
                        title={`อัตราปัจจุบันตามตารางปรับขึ้น (ฐาน ${fmt(c.monthly)})`}
                        style={{ marginLeft: 4 }}
                      >
                        ▲
                      </span>
                    ) : null}
                  </td>
                  <td className="num tnum">{fmt(c.totalValue)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(100, pct)}%`,
                            height: '100%',
                            background: cat.tone,
                          }}
                        />
                      </div>
                      <span className="meta" style={{ minWidth: 36, textAlign: 'right' }}>
                        {pct}%
                      </span>
                    </div>
                  </td>
                  <td className="num">
                    {c.daysLeft <= 0 ? (
                      <span style={{ color: 'var(--negative)', fontWeight: 600 }}>หมดแล้ว</span>
                    ) : c.daysLeft <= 30 ? (
                      <span style={{ color: 'var(--negative)', fontWeight: 600 }}>
                        {c.daysLeft}
                      </span>
                    ) : c.daysLeft <= 90 ? (
                      <span style={{ color: 'var(--warning)', fontWeight: 600 }}>{c.daysLeft}</span>
                    ) : (
                      <span className="muted">{c.daysLeft}</span>
                    )}
                  </td>
                  <td>
                    {c.status === 'active' ? (
                      <span className="badge positive">
                        <span className="bdot" />
                        ใช้งาน
                      </span>
                    ) : c.status === 'expiring' ? (
                      <span className="badge warning">
                        <span className="bdot" />
                        ใกล้หมด
                      </span>
                    ) : c.status === 'expired' ? (
                      <span className="badge negative">
                        <span className="bdot" />
                        หมดอายุ
                      </span>
                    ) : (
                      <span className="badge info">
                        <span className="bdot" />
                        รอเริ่ม
                      </span>
                    )}
                    <OCApprovalBadge approvalStatus={c.approvalStatus} />
                    <ReclassifyBadge
                      needsReclassification={c.needsReclassification}
                      style={{ marginLeft: 6 }}
                    />
                  </td>
                  <td>
                    {c.renewedToCode ? (
                      <span
                        className="badge"
                        style={{ color: 'var(--text-muted)' }}
                        title={`ต่อสัญญาแล้วเป็น ${c.renewedToCode}`}
                      >
                        <span className="bdot" />
                        ต่อแล้ว → {c.renewedToCode}
                      </span>
                    ) : c.status === 'expiring' || c.status === 'expired' ? (
                      <button
                        className="btn sm primary"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onRenew(c)}
                      >
                        ต่อสัญญา →
                      </button>
                    ) : (
                      <button
                        className="btn sm ghost"
                        style={{ height: 28, fontSize: 14 }}
                        onClick={() => onView(c)}
                      >
                        ดู
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td colSpan={5}>รวม {rows.length} สัญญา</td>
              <td />
              <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.monthly, 0))}</td>
              <td className="num tnum">{fmt(rows.reduce((s, r) => s + r.totalValue, 0))}</td>
              <td colSpan={4} />
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface OCScheduleProps {
  rows: readonly OCContract[];
  accruals: readonly OpContractAccrual[];
  periods: readonly PeriodMaster[];
  year: number;
}

function OCSchedule({ rows, accruals, periods, year }: OCScheduleProps) {
  const activeContracts = rows
    .filter((c) => c.status === 'active' || c.status === 'expiring')
    .slice(0, 10);
  const totalsRows = rows.filter((c) => c.status === 'active' || c.status === 'expiring');
  const statusMap = buildAccrualStatusMap([...accruals]);
  const openCodes = openPeriods([...periods]).map((p) => p.period);
  const currentOpenPeriod = openCodes.filter((c) => c.startsWith(String(year))).sort()[0] ?? '';
  const currentMonthIdx = currentOpenPeriod ? Number(currentOpenPeriod.slice(4, 6)) - 1 : -1;

  if (activeContracts.length === 0) {
    return (
      <div className="card">
        <div className="card-body" style={{ padding: 40, textAlign: 'center' }}>
          <div className="muted" style={{ fontSize: 15 }}>
            ยังไม่มีข้อมูลสำหรับช่วงเวลานี้
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          Recognition Schedule — สัญญา × เดือน ปี {year}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          <span className="chip">
            <span
              style={{ width: 8, height: 8, background: '#e7f4ec', border: '1px solid #bbdebf' }}
            />{' '}
            Posted
          </span>
          <span className="chip">
            <span
              style={{ width: 8, height: 8, background: '#fbeae9', border: '1px solid #f0c8c5' }}
            />{' '}
            กลับรายการ
          </span>
          <span className="chip active">
            <span
              style={{
                width: 8,
                height: 8,
                background: 'var(--brand-tint)',
                border: '1px solid var(--brand)',
              }}
            />{' '}
            งวดปัจจุบัน
          </span>
          <span className="chip">
            <span
              style={{
                width: 8,
                height: 8,
                background: '#fafafa',
                border: '1px solid var(--border)',
              }}
            />{' '}
            รอประมวลผล
          </span>
        </div>
      </div>
      <div className="tbl-wrap" style={{ maxHeight: 560, overflowX: 'auto' }}>
        <table className="tbl" style={{ minWidth: 1500 }}>
          <thead>
            <tr>
              <th
                style={{
                  width: 140,
                  position: 'sticky',
                  left: 0,
                  background: '#fafafa',
                  zIndex: 2,
                }}
              >
                สัญญา
              </th>
              <th
                style={{
                  width: 200,
                  position: 'sticky',
                  left: 140,
                  background: '#fafafa',
                  zIndex: 2,
                  borderRight: '2px solid var(--border-strong)',
                }}
              >
                Vendor
              </th>
              {MONTHS.map((m, i) => (
                <th
                  key={m}
                  className="num"
                  style={{
                    width: 95,
                    background: i === currentMonthIdx ? 'var(--brand-tint)' : undefined,
                    color: i === currentMonthIdx ? 'var(--brand)' : undefined,
                  }}
                >
                  {m}
                </th>
              ))}
              <th className="num" style={{ width: 120 }}>
                รวมปี
              </th>
            </tr>
          </thead>
          <tbody>
            {activeContracts.map((c) => {
              const cat = getOCCat(c.cat);
              let yearTotal = 0;
              const cells = MONTHS.map((_, i) => {
                const state = cellStatus(
                  { apiId: c.apiId, start: c.start, end: c.end },
                  i,
                  year,
                  statusMap,
                  currentOpenPeriod,
                );
                const inRange = state !== 'out';
                if (inRange) yearTotal += c.monthly;
                const bg =
                  state === 'posted'
                    ? '#e7f4ec'
                    : state === 'reversed'
                      ? '#fbeae9'
                      : state === 'current'
                        ? 'var(--brand-tint)'
                        : '#fafafa';
                const color =
                  state === 'posted'
                    ? '#117a3d'
                    : state === 'reversed'
                      ? '#b42318'
                      : state === 'current'
                        ? 'var(--brand)'
                        : state === 'pending'
                          ? 'var(--text-muted)'
                          : 'var(--text-faint)';
                return (
                  <td
                    key={i}
                    className="num tnum"
                    style={{
                      background: bg,
                      color,
                      fontWeight: state === 'current' ? 700 : 500,
                      textDecoration: state === 'reversed' ? 'line-through' : undefined,
                    }}
                  >
                    {inRange ? fmt(c.monthly) : '—'}
                  </td>
                );
              });
              return (
                <tr key={c.id}>
                  <td
                    style={{ position: 'sticky', left: 0, background: 'white', zIndex: 1 }}
                    className="code"
                  >
                    {c.id.slice(-6)}
                  </td>
                  <td
                    style={{
                      position: 'sticky',
                      left: 140,
                      background: 'white',
                      zIndex: 1,
                      borderRight: '2px solid var(--border)',
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{ width: 8, height: 8, borderRadius: 2, background: cat.tone }}
                      />
                      <span style={{ fontSize: 13.5, fontWeight: 500 }}>
                        {c.vendor.slice(0, 28)}
                      </span>
                    </div>
                  </td>
                  {cells}
                  <td className="num tnum" style={{ background: '#fafafa', fontWeight: 700 }}>
                    {fmt(yearTotal)}
                  </td>
                </tr>
              );
            })}
            <tr className="total">
              <td
                colSpan={2}
                style={{
                  position: 'sticky',
                  left: 0,
                  background: '#f4f4f7',
                  zIndex: 1,
                  borderRight: '2px solid var(--border-strong)',
                }}
              >
                รวมทุกสัญญา
              </td>
              {MONTHS.map((_, i) => {
                const total = totalsRows.reduce((s, c) => {
                  const inRange =
                    cellStatus(
                      { apiId: c.apiId, start: c.start, end: c.end },
                      i,
                      year,
                      statusMap,
                      currentOpenPeriod,
                    ) !== 'out';
                  return s + (inRange ? c.monthly : 0);
                }, 0);
                return (
                  <td
                    key={i}
                    className="num tnum"
                    style={{
                      background: i === currentMonthIdx ? 'var(--brand)' : '#f4f4f7',
                      color: i === currentMonthIdx ? 'white' : 'inherit',
                    }}
                  >
                    {fmt(total)}
                  </td>
                );
              })}
              <td className="num tnum" style={{ background: 'var(--brand)', color: 'white' }}>
                —
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

interface OCRunHistoryProps {
  companyId: string | undefined;
  accruals: readonly OpContractAccrual[];
  periods: readonly PeriodMaster[];
  onRun: () => void;
  onReverse: (period: string) => void;
}

// M3 maker-checker: surface the accrual RUNS (one approval per monthly run) on
// top of the posted-accrual history. A run is PENDING (awaiting approval — no JV
// posted yet) / APPROVED (JVs posted) / REJECTED / RETURNED. PENDING and
// RETURNED runs offer a "ดำเนินการอนุมัติ" action that opens the run approval
// panel. The posted-accrual grouping (groupAccrualsByPeriod) below still drives
// the reverse action for POSTED periods.
export function OCRunHistory({
  companyId,
  accruals,
  periods,
  onRun,
  onReverse,
}: OCRunHistoryProps) {
  const runs = groupAccrualsByPeriod([...accruals], [...periods]);
  const { data: accrualRuns = [] } = useAccrualRuns(companyId);
  const [approveRun, setApproveRun] = useState<OpContractAccrualRun | null>(null);

  const runBadge = (status: string | null | undefined) => {
    if (status === 'APPROVED') {
      return (
        <span className="badge positive">
          <Icon name="check" size={10} /> อนุมัติแล้ว
        </span>
      );
    }
    if (status === 'REJECTED') {
      return (
        <span className="badge negative">
          <span className="bdot" />
          ถูกปฏิเสธ
        </span>
      );
    }
    if (status === 'RETURNED') {
      return (
        <span className="badge warning">
          <span className="bdot" />
          ตีกลับแก้ไข
        </span>
      );
    }
    // PENDING / DRAFT / null → awaiting approval.
    return (
      <span className="badge warning">
        <span className="bdot" />
        รออนุมัติ
      </span>
    );
  };

  return (
    <>
      {accrualRuns.length > 0 && (
        <div className="tbl-shell" style={{ marginBottom: 'var(--section-gap)' }}>
          <div className="filter-row">
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              การรับรู้รอ/ผ่านการอนุมัติ — งวดที่ส่งเข้าสายอนุมัติ
            </span>
          </div>
          <table className="tbl">
            <thead>
              <tr>
                <th>งวด</th>
                <th className="num">จำนวนสัญญา</th>
                <th className="num">มูลค่ารวม</th>
                <th>ผู้ส่งอนุมัติ</th>
                <th>ผู้อนุมัติ</th>
                <th>สถานะ</th>
                <th>การกระทำ</th>
              </tr>
            </thead>
            <tbody>
              {accrualRuns.map((r) => {
                const label = formatPeriodLabel(r.period, [...periods]);
                const status = r.approvalStatus ?? null;
                const actionable = status === 'PENDING' || status === 'RETURNED';
                return (
                  <tr key={r.id}>
                    <td>
                      <b>{label}</b>
                    </td>
                    <td className="num">{r.contractCount}</td>
                    <td className="num tnum">{fmt(Number(r.totalAmount))}</td>
                    <td className="muted">{r.submittedBy ?? '—'}</td>
                    <td className="muted">{r.approvedBy ?? '—'}</td>
                    <td>{runBadge(status)}</td>
                    <td>
                      <button
                        className={actionable ? 'btn sm primary' : 'btn sm ghost'}
                        style={{ height: 26, fontSize: 13 }}
                        onClick={() => setApproveRun(r)}
                      >
                        {actionable ? 'เปิดสายอนุมัติ' : 'สายอนุมัติ'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            ประวัติการรับรู้รายเดือน — สัญญาดำเนินงาน (Posted)
          </span>
          <div style={{ marginLeft: 'auto' }}>
            <button className="btn sm primary" onClick={onRun}>
              <Icon name="check" size={12} /> ประมวลผลงวด
            </button>
          </div>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>งวด</th>
              <th>วันที่ลงบัญชี</th>
              <th className="num">จำนวนสัญญา</th>
              <th className="num">มูลค่ารวม</th>
              <th>ผู้ประมวลผล</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {runs.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีประวัติการรับรู้ — กดปุ่ม &quot;<b>ประมวลผลงวด</b>&quot; เพื่อรันงวดแรก
                </td>
              </tr>
            ) : (
              runs.map((r) => (
                <tr key={r.period}>
                  <td>
                    <b>{r.label}</b>
                  </td>
                  <td className="muted">
                    {r.postedAt ? fmtDate(String(r.postedAt).slice(0, 10)) : '—'}
                  </td>
                  <td className="num">{r.count}</td>
                  <td className="num tnum">{fmt(r.totalAmount)}</td>
                  <td className="muted">{r.postedBy ?? '—'}</td>
                  <td>
                    {r.status === 'REVERSED' ? (
                      <span className="badge negative">
                        <span className="bdot" />
                        กลับรายการแล้ว
                      </span>
                    ) : r.status === 'MIXED' ? (
                      <span className="badge warning">
                        <span className="bdot" />
                        กลับบางส่วน
                      </span>
                    ) : (
                      <span className="badge positive">
                        <Icon name="check" size={10} /> Posted
                      </span>
                    )}
                  </td>
                  <td>
                    {r.postedCount > 0 ? (
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13 }}
                        onClick={() => onReverse(r.period)}
                      >
                        กลับรายการ
                      </button>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {approveRun && companyId && (
        <OpContractRunApprovalPanel
          companyId={companyId}
          run={approveRun}
          periodLabel={formatPeriodLabel(approveRun.period, [...periods])}
          onClose={() => setApproveRun(null)}
        />
      )}
    </>
  );
}

interface ContractDetailModalProps {
  companyId: string | undefined;
  contractId: string;
  onClose: () => void;
  onReverse: (period: string) => void;
}

export function ContractDetailModal({
  companyId,
  contractId,
  onClose,
  onReverse,
}: ContractDetailModalProps) {
  const { data, isPending, error } = useOpContract(companyId, contractId);
  const cat = data ? getOCCat(data.contractType as CatCode) : OC_FALLBACK;
  const [showApproval, setShowApproval] = useState<boolean>(false);
  // M3: the contract is in an engine-approval flow when approvalStatus is set
  // and not the terminal APPROVED (PENDING / DRAFT / RETURNED / REJECTED).
  const inApprovalFlow = !!data?.approvalStatus && data.approvalStatus !== 'APPROVED';
  return (
    <>
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(15,15,20,0.45)',
          display: 'grid',
          placeItems: 'center',
          zIndex: 100,
          padding: 24,
        }}
        onClick={onClose}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: 'white',
            borderRadius: 12,
            width: 860,
            maxWidth: '100%',
            boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
            overflow: 'hidden',
            maxHeight: '94vh',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <div
            style={{
              padding: '18px 22px',
              borderBottom: '1px solid var(--border)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <div>
              <h3
                style={{
                  margin: 0,
                  fontSize: 18,
                  fontWeight: 600,
                  fontFamily: 'var(--font-display)',
                }}
              >
                รายละเอียดสัญญา
                {data ? (
                  <>
                    {' '}
                    — <span>{data.code}</span>
                  </>
                ) : (
                  ''
                )}
              </h3>
              <div className="meta" style={{ marginTop: 2 }}>
                {data ? (
                  <>
                    <span>{data.vendorName ?? '—'}</span> · {cat.label}
                    <ReclassifyBadge
                      needsReclassification={data.needsReclassification}
                      style={{ marginLeft: 8 }}
                    />
                  </>
                ) : (
                  'กำลังโหลด…'
                )}
              </div>
            </div>
            <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
              <Icon name="x" size={18} />
            </button>
          </div>
          <div
            style={{
              padding: 22,
              overflow: 'auto',
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              gap: 16,
            }}
          >
            {isPending ? (
              <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                กำลังโหลด…
              </div>
            ) : error ? (
              <div
                className="muted"
                style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
              >
                โหลดล้มเหลว: {error.message}
              </div>
            ) : data ? (
              <>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3,1fr)',
                    gap: 12,
                    fontSize: 14,
                  }}
                >
                  <DetailField label="สาขา" value={data.branchName ?? '—'} />
                  <DetailField label="บัญชีค่าใช้จ่าย" value={data.expenseAccountCode} />
                  <DetailField label="สถานะ" value={data.status} />
                  <DetailField
                    label="เริ่ม → สิ้นสุด"
                    value={`${fmtDate(String(data.startDate).slice(0, 10))} → ${fmtDate(String(data.endDate).slice(0, 10))}`}
                  />
                  <DetailField label="ค่าใช้จ่าย/เดือน" value={fmt(Number(data.monthlyAmount))} />
                  <DetailField
                    label="รับรู้แล้ว"
                    value={`${data.monthsAccrued}/${data.monthsTotal} งวด · ${fmt(Number(data.accruedAmount))}`}
                  />
                </div>
                {inApprovalFlow && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 12,
                      padding: '12px 16px',
                      background: '#fef3e8',
                      border: '1px solid #f0dab0',
                      borderRadius: 8,
                    }}
                    data-testid="osc-approval-banner"
                  >
                    <Icon name="check" size={14} />
                    <div style={{ flex: 1, fontSize: 14 }}>
                      <b>
                        {data.pendingAction === 'TERMINATE'
                          ? 'รออนุมัติยกเลิกสัญญา'
                          : data.pendingAction === 'REGISTER'
                            ? 'รออนุมัติทะเบียนสัญญา'
                            : data.approvalStatus === 'RETURNED'
                              ? 'ถูกตีกลับให้แก้ไข'
                              : data.approvalStatus === 'REJECTED'
                                ? 'ถูกปฏิเสธ'
                                : 'อยู่ระหว่างการอนุมัติ'}
                      </b>{' '}
                      <span className="meta">· สถานะ {data.approvalStatus}</span>
                    </div>
                    <button className="btn sm primary" onClick={() => setShowApproval(true)}>
                      สายอนุมัติ
                    </button>
                  </div>
                )}
                {(() => {
                  const dd = contractDrawdown(
                    Number(data.monthlyAmount),
                    data.monthsTotal,
                    data.accruedAmount,
                  );
                  return (
                    <div
                      style={{
                        padding: '14px 16px',
                        background: '#fafafa',
                        border: '1px solid var(--border)',
                        borderRadius: 8,
                      }}
                    >
                      <div className="section-title" style={{ margin: '0 0 10px' }}>
                        มูลค่าสัญญา · Drawdown
                      </div>
                      <div
                        style={{
                          display: 'grid',
                          gridTemplateColumns: 'repeat(3,1fr)',
                          gap: 12,
                          fontSize: 14,
                          marginBottom: 10,
                        }}
                      >
                        <div>
                          <div className="meta">มูลค่ารวม</div>
                          <div className="tnum" style={{ fontWeight: 600, marginTop: 2 }}>
                            {fmt(dd.total)}
                          </div>
                        </div>
                        <div>
                          <div className="meta">รับรู้แล้ว</div>
                          <div
                            className="tnum"
                            style={{ fontWeight: 600, marginTop: 2, color: 'var(--brand)' }}
                          >
                            {fmt(dd.recognized)}
                          </div>
                        </div>
                        <div>
                          <div className="meta">คงเหลือ</div>
                          <div className="tnum" style={{ fontWeight: 600, marginTop: 2 }}>
                            {fmt(dd.remaining)}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div
                          style={{
                            flex: 1,
                            height: 6,
                            borderRadius: 3,
                            background: '#f0f0f3',
                            overflow: 'hidden',
                          }}
                        >
                          <div
                            style={{
                              width: `${dd.pct}%`,
                              height: '100%',
                              background: 'var(--brand)',
                            }}
                          />
                        </div>
                        <span className="meta" style={{ minWidth: 36, textAlign: 'right' }}>
                          {dd.pct}%
                        </span>
                      </div>
                    </div>
                  );
                })()}
                {data.renewedFromCode || (data.renewalCodes && data.renewalCodes.length > 0) ? (
                  <div className="meta" style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                    {data.renewedFromCode ? <span>ต่อจาก {data.renewedFromCode}</span> : null}
                    {data.renewalCodes && data.renewalCodes.length > 0 ? (
                      <span>ต่อเป็น {data.renewalCodes.join(', ')}</span>
                    ) : null}
                  </div>
                ) : null}
                {data.notes ? <div className="meta">หมายเหตุ: {data.notes}</div> : null}
                <div>
                  <div className="section-title" style={{ margin: '0 0 8px' }}>
                    รายการรับรู้รายเดือน
                  </div>
                  <div
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}
                  >
                    <table className="tbl">
                      <thead>
                        <tr>
                          <th>งวด</th>
                          <th className="num">จำนวน</th>
                          <th>สถานะ</th>
                          <th>การกระทำ</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.accruals.length === 0 ? (
                          <tr>
                            <td
                              colSpan={4}
                              className="muted"
                              style={{ padding: 20, textAlign: 'center' }}
                            >
                              ยังไม่มีการรับรู้
                            </td>
                          </tr>
                        ) : (
                          data.accruals.map((a) => (
                            <tr key={a.id}>
                              <td className="code">
                                {a.period}
                                {a.trueUpBillVoucherNo ? (
                                  <span
                                    className="badge info"
                                    style={{ marginLeft: 6 }}
                                    title={`จับคู่บิล ${a.trueUpBillVoucherNo}`}
                                  >
                                    🔗 {a.trueUpBillVoucherNo}
                                  </span>
                                ) : null}
                              </td>
                              <td className="num tnum">{fmt(Number(a.amount))}</td>
                              <td>
                                {a.status === 'POSTED' ? (
                                  <span className="badge positive">
                                    <Icon name="check" size={10} /> Posted
                                  </span>
                                ) : a.status === 'REVERSED' ? (
                                  <span className="badge negative">
                                    <span className="bdot" />
                                    กลับรายการ
                                  </span>
                                ) : (
                                  <span className="badge warning">
                                    <span className="bdot" />
                                    รอประมวลผล
                                  </span>
                                )}
                              </td>
                              <td>
                                {a.status === 'POSTED' ? (
                                  <button
                                    className="btn sm ghost"
                                    style={{ height: 26, fontSize: 13 }}
                                    onClick={() => onReverse(a.period)}
                                  >
                                    กลับรายการ
                                  </button>
                                ) : (
                                  <span className="muted">—</span>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
                <RateScheduleSection
                  companyId={companyId}
                  contractId={contractId}
                  baseMonthly={Number(data.monthlyAmount)}
                  currentRate={data.currentRate != null ? Number(data.currentRate) : undefined}
                  startPeriod={String(data.startDate).slice(0, 7).replace('-', '')}
                />
              </>
            ) : null}
          </div>
          <div
            style={{
              padding: '14px 22px',
              borderTop: '1px solid var(--border)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
              background: '#fafafa',
            }}
          >
            <button className="btn ghost" onClick={onClose}>
              ปิด
            </button>
          </div>
        </div>
      </div>
      {showApproval && data && companyId && (
        <OpContractApprovalPanel
          companyId={companyId}
          contract={data}
          onClose={() => setShowApproval(false)}
        />
      )}
    </>
  );
}

// M5a escalation — the effective-dated rate-schedule section in the detail
// modal. Lists the contract's rate rows (effectiveFrom → amount → note) with a
// delete action, an inline add-row form, and surfaces the contract's current
// effective rate vs its base monthlyAmount. The accrual already reads the
// period-effective rate server-side; this section just maintains the schedule.
function RateScheduleSection({
  companyId,
  contractId,
  baseMonthly,
  currentRate,
  startPeriod,
}: {
  companyId: string | undefined;
  contractId: string;
  baseMonthly: number;
  currentRate?: number | undefined;
  startPeriod: string;
}) {
  const { data: rates = [], isPending } = useOpContractRates(companyId, contractId);
  const addRate = useAddOpContractRate(companyId, contractId);
  const deleteRate = useDeleteOpContractRate(companyId, contractId);

  const [effFrom, setEffFrom] = useState<string>(''); // <input type=month> value 'YYYY-MM'
  const [amount, setAmount] = useState<string>('');
  const [note, setNote] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);

  const effPeriod = effFrom.replace('-', ''); // 'YYYYMM'
  const amt = Number(amount);
  const canAdd =
    /^\d{6}$/.test(effPeriod) && effPeriod >= startPeriod && Number.isFinite(amt) && amt > 0;

  const submit = () => {
    setErr(null);
    if (!canAdd) {
      setErr('ระบุงวดเริ่มผล (≥ งวดเริ่มสัญญา) และจำนวนเงิน > 0');
      return;
    }
    addRate.mutate(
      {
        effectiveFrom: effPeriod,
        monthlyAmount: amt,
        ...(note.trim() ? { note: note.trim() } : {}),
      },
      {
        onSuccess: () => {
          setEffFrom('');
          setAmount('');
          setNote('');
        },
        onError: (e) => setErr(e instanceof Error ? e.message : 'เพิ่มอัตราไม่สำเร็จ'),
      },
    );
  };

  const effectiveLabel =
    currentRate != null && currentRate !== baseMonthly
      ? `อัตราปัจจุบัน ${fmt(currentRate)} (ฐาน ${fmt(baseMonthly)})`
      : `อัตราปัจจุบัน ${fmt(currentRate ?? baseMonthly)}`;

  return (
    <div data-testid="osc-rate-schedule">
      <div
        className="section-title"
        style={{ margin: '0 0 8px', display: 'flex', alignItems: 'center', gap: 10 }}
      >
        ตารางอัตราค่าบริการ (Escalation)
        <span
          className="badge"
          style={{ fontWeight: 500, background: '#eef4ff', color: 'var(--brand)' }}
          data-testid="osc-current-rate"
        >
          {effectiveLabel}
        </span>
      </div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>เริ่มมีผล (งวด)</th>
              <th className="num">อัตรา/เดือน</th>
              <th>หมายเหตุ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                  กำลังโหลด…
                </td>
              </tr>
            ) : rates.length === 0 ? (
              <tr>
                <td colSpan={4} className="muted" style={{ padding: 16, textAlign: 'center' }}>
                  ยังไม่มีการปรับอัตรา — ใช้อัตราฐาน {fmt(baseMonthly)} ทุกงวด
                </td>
              </tr>
            ) : (
              rates.map((r) => (
                <tr key={r.id}>
                  <td className="code">{formatPeriodLabel(r.effectiveFrom, [])}</td>
                  <td className="num tnum">{fmt(Number(r.monthlyAmount))}</td>
                  <td className="muted">{r.note ?? '—'}</td>
                  <td>
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      disabled={deleteRate.isPending}
                      onClick={() => deleteRate.mutate(r.id)}
                    >
                      ลบ
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <div
        style={{
          marginTop: 10,
          display: 'flex',
          alignItems: 'flex-end',
          gap: 10,
          flexWrap: 'wrap',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          <span className="meta">เริ่มมีผล</span>
          <input
            type="month"
            className="input"
            aria-label="เริ่มมีผล"
            value={effFrom}
            min={`${startPeriod.slice(0, 4)}-${startPeriod.slice(4, 6)}`}
            onChange={(e) => setEffFrom(e.target.value)}
            style={{ height: 32 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
          <span className="meta">อัตรา/เดือน</span>
          <input
            type="number"
            className="input"
            aria-label="อัตราต่อเดือน"
            value={amount}
            min={0}
            step="0.01"
            onChange={(e) => setAmount(e.target.value)}
            style={{ height: 32, width: 130 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13, flex: 1 }}>
          <span className="meta">หมายเหตุ (ถ้ามี)</span>
          <input
            type="text"
            className="input"
            aria-label="หมายเหตุอัตรา"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ height: 32 }}
          />
        </label>
        <button
          className="btn primary"
          style={{ height: 32 }}
          disabled={addRate.isPending || !canAdd}
          onClick={submit}
        >
          เพิ่มอัตรา
        </button>
      </div>
      {err ? (
        <div className="meta" style={{ marginTop: 6, color: 'var(--negative)' }}>
          {err}
        </div>
      ) : null}
    </div>
  );
}

function DetailField({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: '#fafafa',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '8px 10px',
      }}
    >
      <div className="meta">{label}</div>
      <div style={{ fontWeight: 600, marginTop: 2 }}>{value}</div>
    </div>
  );
}

// M4 TFRS 16 reclassification badge — a short-term-exempt contract whose
// per-contract term exceeds 12 months can no longer use the short-term
// exemption and must be reassessed for capitalization. Renders nothing unless
// the row carries needsReclassification.
const RECLASSIFY_TITLE =
  'สัญญาสั้น (Short-term) ที่มีอายุเกิน 12 เดือน — ต้องทบทวนเพื่อบันทึกเป็นสินทรัพย์ตาม TFRS 16';

function ReclassifyBadge({
  needsReclassification: flag,
  style,
}: {
  needsReclassification?: boolean | undefined;
  style?: React.CSSProperties;
}) {
  if (!flag) return null;
  return (
    <span
      className="badge negative"
      style={style}
      title={RECLASSIFY_TITLE}
      data-testid="osc-reclassify-badge"
    >
      ⚠ ทบทวน TFRS16
    </span>
  );
}

// M3 maker-checker register badge — surfaces the engine approvalStatus on a
// register row. APPROVED / null render nothing (the row looks as it did pre-M3).
function OCApprovalBadge({ approvalStatus }: { approvalStatus?: string | null | undefined }) {
  if (!approvalStatus || approvalStatus === 'APPROVED') return null;
  if (approvalStatus === 'REJECTED') {
    return (
      <span className="badge negative" style={{ marginLeft: 6 }}>
        <span className="bdot" />
        ถูกปฏิเสธ
      </span>
    );
  }
  if (approvalStatus === 'RETURNED') {
    return (
      <span className="badge warning" style={{ marginLeft: 6 }}>
        <span className="bdot" />
        ตีกลับแก้ไข
      </span>
    );
  }
  // PENDING / DRAFT → awaiting approval.
  return (
    <span className="badge warning" style={{ marginLeft: 6 }}>
      <span className="bdot" />
      รออนุมัติ
    </span>
  );
}

interface ReverseConfirmModalProps {
  companyId: string | undefined;
  period: string;
  periods: readonly PeriodMaster[];
  onClose: () => void;
}

export function ReverseConfirmModal({
  companyId,
  period,
  periods,
  onClose,
}: ReverseConfirmModalProps) {
  const reverseMut = useReverseOpContracts(companyId);
  const label = formatPeriodLabel(period, [...periods]);
  const confirm = () => {
    if (!companyId) return;
    reverseMut.mutate({ data: { period } }, { onSuccess: () => onClose() });
  };
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 110,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 460,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '18px 22px', borderBottom: '1px solid var(--border)' }}>
          <h3
            style={{ margin: 0, fontSize: 17, fontWeight: 600, fontFamily: 'var(--font-display)' }}
          >
            กลับรายการรับรู้ — งวด {label}
          </h3>
        </div>
        <div style={{ padding: 22, fontSize: 14, lineHeight: 1.6 }}>
          ระบบจะโพสต์ JV กลับรายการ (<span className="code muted">Dr 20170001 / Cr ค่าใช้จ่าย</span>
          ) ของงวดนี้ ลงในงวดถัดไป รายการที่ Posted แล้วทั้งหมดของงวด {label} จะถูกตั้งเป็น REVERSED
          · ดำเนินการนี้ idempotent
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={confirm}
            disabled={reverseMut.isPending || !companyId}
          >
            <Icon name="check" size={14} />{' '}
            {reverseMut.isPending ? 'กำลังกลับรายการ...' : 'ยืนยันกลับรายการ'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface OCRenewalProps {
  rows: readonly OCContract[];
  onRenew: (c: OCContract) => void;
}

function OCRenewal({ rows, onRenew }: OCRenewalProps) {
  // Already-renewed contracts (renewedToCode set) no longer need action — the
  // successor is registered and the predecessor keeps accruing to its endDate.
  // Drop them from the upcoming list AND the KPI counts.
  const actionable = rows.filter((c) => !c.renewedToCode);
  const upcoming = actionable
    .filter((c) => c.status === 'expiring' || c.status === 'expired')
    .sort((a, b) => a.daysLeft - b.daysLeft);
  return (
    <>
      <div className="grid-3" style={{ marginBottom: 'var(--section-gap)' }}>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--negative)' }} />
            หมดแล้ว · ยังไม่ต่อ
          </div>
          <div className="value tnum">
            {actionable.filter((c) => c.status === 'expired').length}
          </div>
          <div className="trend down">ควรเร่งต่อสัญญา/ยกเลิก</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--warning)' }} />
            30 วันข้างหน้า
          </div>
          <div className="value tnum">
            {actionable.filter((c) => c.status === 'expiring' && c.daysLeft <= 30).length}
          </div>
          <div className="trend faint">เตรียมต่อสัญญา</div>
        </div>
        <div className="kpi">
          <div className="label">
            <span className="dot" style={{ background: 'var(--info)' }} />
            31-90 วันข้างหน้า
          </div>
          <div className="value tnum">
            {actionable.filter((c) => c.status === 'expiring' && c.daysLeft > 30).length}
          </div>
          <div className="trend faint">เริ่มเจรจาราคา</div>
        </div>
      </div>

      <div className="tbl-shell">
        <div className="filter-row">
          <span style={{ fontSize: 14, fontWeight: 600 }}>
            สัญญาที่ใกล้หมดอายุ — เรียงตามวันที่เหลือ
          </span>
        </div>
        <table className="tbl">
          <thead>
            <tr>
              <th>สัญญา</th>
              <th>Vendor</th>
              <th>ประเภท</th>
              <th>วันสิ้นสุด</th>
              <th>แจ้งยกเลิกภายใน</th>
              <th>เหลือ (วัน)</th>
              <th className="num">ค่าใช้จ่าย/เดือน</th>
              <th>การต่อสัญญา</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {upcoming.length === 0 && (
              <tr>
                <td colSpan={10} className="muted" style={{ padding: 32, textAlign: 'center' }}>
                  ยังไม่มีสัญญาที่ใกล้หมดอายุ
                </td>
              </tr>
            )}
            {upcoming.map((c) => {
              const cat = getOCCat(c.cat);
              const notice = noticeInfo(
                { endDate: c.end, noticePeriodDays: c.noticePeriodDays, autoRenew: c.autoRenew },
                OC_TODAY,
              );
              return (
                <tr key={c.id}>
                  <td className="code">
                    <a style={{ color: 'var(--brand)', fontWeight: 500 }}>{c.id}</a>
                  </td>
                  <td>
                    <b>{c.vendor}</b>
                    <div className="meta">{c.desc.slice(0, 50)}</div>
                  </td>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <span
                        style={{ width: 8, height: 8, borderRadius: 2, background: cat.tone }}
                      />
                      {cat.label}
                    </span>
                  </td>
                  <td>{fmtDate(c.end)}</td>
                  <td>
                    {notice.noticeDate ? (
                      <>
                        {fmtDate(notice.noticeDate)}
                        <div
                          className="meta"
                          style={{
                            color:
                              notice.noticeDaysLeft != null && notice.noticeDaysLeft <= 0
                                ? 'var(--negative)'
                                : undefined,
                            fontWeight:
                              notice.noticeDaysLeft != null && notice.noticeDaysLeft <= 0
                                ? 600
                                : undefined,
                          }}
                        >
                          {notice.noticeDaysLeft != null && notice.noticeDaysLeft <= 0
                            ? 'เลยกำหนดแจ้ง'
                            : `อีก ${notice.noticeDaysLeft} วัน`}
                        </div>
                      </>
                    ) : (
                      <span className="muted">—</span>
                    )}
                  </td>
                  <td className="num">
                    <b
                      style={{
                        color:
                          c.daysLeft <= 0
                            ? 'var(--negative)'
                            : c.daysLeft <= 30
                              ? 'var(--warning)'
                              : 'var(--text)',
                      }}
                    >
                      {c.daysLeft <= 0 ? 'หมดแล้ว' : c.daysLeft}
                    </b>
                  </td>
                  <td className="num tnum">{fmt(c.monthly)}</td>
                  <td>
                    {notice.willAutoRenew ? (
                      <span className="badge warning">
                        <span className="bdot" />
                        ต่อออโต้
                      </span>
                    ) : (
                      <span className="badge">
                        <span className="bdot" />
                        ต้องต่อเอง
                      </span>
                    )}
                  </td>
                  <td>
                    {c.status === 'expired' ? (
                      <span className="badge negative">
                        <span className="bdot" />
                        หมดอายุ
                      </span>
                    ) : (
                      <span className="badge warning">
                        <span className="bdot" />
                        ใกล้หมด
                      </span>
                    )}
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      <button
                        className="btn sm primary"
                        style={{ height: 26, fontSize: 13 }}
                        onClick={() => onRenew(c)}
                      >
                        ต่อสัญญา
                      </button>
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13 }}
                        onClick={() => {
                          /* TODO(phase-3): wire */
                        }}
                      >
                        ยกเลิก
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

// ============================================================
// M4 TFRS 16 — para-53 disclosure view (5th tab "เปิดเผย TFRS 16").
//
// Driven by useOpContractDisclosure(companyId, year). Surfaces the para-53
// note: short-term lease expense (para 53(c)) + low-value asset lease expense
// (para 53(d)); plus a per-basis breakdown table. SERVICE_NON_LEASE /
// UNCLASSIFIED are kept in a clearly-separated "ไม่ใช่รายการเปิดเผย para 53"
// group (non-lease / unclassified — NOT part of the para-53 totals).
// ============================================================

// basis code → Thai label. SHORT_TERM/LOW_VALUE/SERVICE_NON_LEASE reuse the
// EXEMPTION_BASES labels; UNCLASSIFIED is a server-only synthetic group (null).
const DISCLOSURE_BASIS_LABEL: Record<string, string> = {
  ...Object.fromEntries(EXEMPTION_BASES.map((b) => [b.value, b.label])),
  UNCLASSIFIED: 'ยังไม่ระบุ (Unclassified)',
};

// The para-53 lease-exemption bases; everything else is shown separately.
const PARA53_BASES = new Set(['SHORT_TERM', 'LOW_VALUE']);

interface OCDisclosureProps {
  companyId: string | undefined;
  year: number;
}

function OCDisclosure({ companyId, year }: OCDisclosureProps) {
  const { data, isPending, error } = useOpContractDisclosure(companyId, year);
  const para53Rows = (data?.byBasis ?? []).filter((b) => PARA53_BASES.has(b.basis));
  const otherRows = (data?.byBasis ?? []).filter((b) => !PARA53_BASES.has(b.basis));

  const basisLabel = (basis: string) => DISCLOSURE_BASIS_LABEL[basis] ?? basis;

  const breakdownTable = (
    rows: OpContractDisclosure['byBasis'],
    emptyText: string,
  ): React.ReactNode => (
    <table className="tbl">
      <thead>
        <tr>
          <th>เหตุยกเว้น (Exemption basis)</th>
          <th className="num">จำนวนสัญญา</th>
          <th className="num">ค่าใช้จ่ายที่รับรู้ (Posted)</th>
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 ? (
          <tr>
            <td colSpan={3} className="muted" style={{ padding: 20, textAlign: 'center' }}>
              {emptyText}
            </td>
          </tr>
        ) : (
          rows.map((b) => (
            <tr key={b.basis}>
              <td>{basisLabel(b.basis)}</td>
              <td className="num">{b.contractCount}</td>
              <td className="num tnum">{fmt(Number(b.recognizedAmount))}</td>
            </tr>
          ))
        )}
      </tbody>
    </table>
  );

  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          เปิดเผยข้อมูลตาม TFRS 16 ย่อหน้า 53 — ปี {year}
        </span>
      </div>
      <div style={{ padding: 18 }}>
        {isPending ? (
          <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
            กำลังโหลด…
          </div>
        ) : error ? (
          <div
            className="muted"
            style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
          >
            โหลดล้มเหลว: {error.message}
          </div>
        ) : data ? (
          <>
            {/* para-53 summary cards */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2,1fr)',
                gap: 12,
                marginBottom: 18,
              }}
            >
              <div
                style={{
                  padding: '14px 16px',
                  background: '#fafafa',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                }}
              >
                <div className="meta">
                  ค่าใช้จ่ายสัญญาสั้น · Short-term lease expense [para 53(c)]
                </div>
                <div className="tnum" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {fmt(Number(data.shortTermTotal))} <span className="unit">บาท</span>
                </div>
              </div>
              <div
                style={{
                  padding: '14px 16px',
                  background: '#fafafa',
                  border: '1px solid var(--border)',
                  borderRadius: 8,
                }}
              >
                <div className="meta">
                  สินทรัพย์มูลค่าต่ำ · Low-value asset lease expense [para 53(d)]
                </div>
                <div className="tnum" style={{ fontSize: 22, fontWeight: 700, marginTop: 4 }}>
                  {fmt(Number(data.lowValueTotal))} <span className="unit">บาท</span>
                </div>
              </div>
            </div>

            {/* para-53 per-basis breakdown */}
            <div className="section-title" style={{ margin: '0 0 8px' }}>
              รายละเอียดตาม para 53 (ค่าใช้จ่ายที่รับรู้จริง · Posted)
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {breakdownTable(para53Rows, 'ไม่มีรายการเปิดเผยตาม para 53 ในปีนี้')}
            </div>

            {/* non-para-53 group — clearly separated */}
            <div className="section-title" style={{ margin: '18px 0 8px' }}>
              ไม่ใช่รายการเปิดเผย para 53 — สัญญาบริการ / ยังไม่ระบุ (non-lease / unclassified)
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
              {breakdownTable(otherRows, 'ไม่มีรายการในกลุ่มนี้')}
            </div>

            <div className="meta" style={{ marginTop: 12, fontSize: 12.5 }}>
              ตัวเลขรวมเฉพาะรายการที่ลงบัญชีแล้ว (Posted) ในปีงบประมาณ {year} ·
              กลุ่มสัญญาบริการ/ยังไม่ระบุไม่นับรวมในยอดเปิดเผย para 53(c)/(d)
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

interface NewContractModalProps {
  companyId: string | undefined;
  onClose: () => void;
}

function NewContractModal({ companyId, onClose }: NewContractModalProps) {
  const [cat, setCat] = useState<CatCode>(FIRST_CAT.code);
  const [months, setMonths] = useState<number>(12);
  const [monthly, setMonthly] = useState<number>(50_000);
  const [startDate, setStartDate] = useState<string>('2026-06-01');
  const [branchId, setBranchId] = useState<string>('');
  const [departmentId, setDepartmentId] = useState<string>('');
  const [expenseAccountCode, setExpenseAccountCode] = useState<string>('');
  const [vendorId, setVendorId] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [noticePeriodDays, setNoticePeriodDays] = useState<number>(30);
  const [autoRenew, setAutoRenew] = useState<boolean>(false);
  // M4 TFRS 16 — '' = unclassified (no exemption basis sent).
  const [exemptionBasis, setExemptionBasis] = useState<OpContractExemptionBasis | ''>('');
  const { data: vendors = [] } = useVendors(companyId);
  const vOpts = useMemo(() => vendorOptions(vendors), [vendors]);
  const createMut = useCreateOpContract(companyId);

  // Postable expense accounts from the real COA (control/header accounts can't
  // be posted). OC_CATS still drives the contractType enum + tone/label cards;
  // the GL code posted is now whatever the user picks from this list.
  const { data: coa = [] } = useCoa(companyId, true);
  const expenseAccounts = useMemo(
    () => coa.filter((a) => (a.type === 'EXPENSE' || a.type === 'COST') && !a.isControl),
    [coa],
  );

  const submit = () => {
    if (!companyId || !expenseAccountCode) return;
    createMut.mutate(
      buildCreateContractPayload({
        contractType: cat,
        startDate,
        months,
        monthly,
        expenseAccountCode,
        noticePeriodDays,
        autoRenew,
        ...(exemptionBasis ? { exemptionBasis } : {}),
        ...(vendorId ? { vendorId } : {}),
        ...(branchId ? { branchId } : {}),
        ...(departmentId ? { departmentId } : {}),
        ...(notes ? { notes } : {}),
      }),
      { onSuccess: () => onClose() },
    );
  };
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 780,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              เพิ่มสัญญาดำเนินงานใหม่
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              เลขที่ใหม่ <b style={{ color: '#3f3f46' }}>OSC-2026-000016</b> · ไม่เข้า TFRS 16 ·
              ลงเป็นค่าใช้จ่ายตรงๆ
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            padding: '22px',
            overflow: 'auto',
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              ประเภทสัญญา
            </label>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 8 }}>
              {OC_CATS.map((o) => (
                <div
                  key={o.code}
                  onClick={() => setCat(o.code)}
                  style={{
                    border: cat === o.code ? '2px solid var(--brand)' : '1.5px solid var(--border)',
                    background: cat === o.code ? 'var(--brand-tint)' : 'white',
                    borderRadius: 8,
                    padding: '10px 8px',
                    cursor: 'pointer',
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: o.tone }} />
                    <span
                      style={{
                        fontSize: 13.5,
                        fontWeight: cat === o.code ? 600 : 500,
                        lineHeight: 1.2,
                      }}
                    >
                      {o.label}
                    </span>
                  </div>
                  <div className="meta" style={{ fontSize: 12 }}>
                    → {o.acct}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              บัญชีค่าใช้จ่าย (GL Code)
            </label>
            <select
              className="select"
              value={expenseAccountCode}
              onChange={(e) => setExpenseAccountCode(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option value="">— เลือกบัญชีค่าใช้จ่าย —</option>
              {expenseAccounts.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.nameTh}
                </option>
              ))}
            </select>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1.5fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                คู่สัญญา / Vendor
              </label>
              <SearchSelect
                value={vendorId}
                onChange={setVendorId}
                options={vOpts}
                getLabel={(id) => vOpts.find((o) => o.value === id)?.label ?? ''}
                placeholder="เลือกคู่สัญญา / Vendor"
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                สาขา
              </label>
              <BranchSelect
                companyId={companyId}
                value={branchId}
                onChange={setBranchId}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              หน่วยงาน (Cost Center)
            </label>
            <DepartmentSelect
              companyId={companyId}
              value={departmentId}
              onChange={setDepartmentId}
              branchId={branchId}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              เหตุที่ไม่เข้า TFRS 16 (Exemption basis)
            </label>
            <select
              className="select"
              value={exemptionBasis}
              onChange={(e) => setExemptionBasis(e.target.value as OpContractExemptionBasis | '')}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option value="">— ยังไม่ระบุ —</option>
              {EXEMPTION_BASES.map((b) => (
                <option key={b.value} value={b.value}>
                  {b.label}
                </option>
              ))}
            </select>
            <div className="meta" style={{ marginTop: 6, fontSize: 12.5 }}>
              อ้างอิงหลักการ S / L / T ด้านบน — Service (ไม่มี Right of Use), Low-value (มูลค่าต่ำ),
              Short-term (สั้น ≤12 เดือน) · ลงเป็นค่าใช้จ่ายตรงๆ ไม่มี ROU / Lease Liability
            </div>
            {exemptionBasis === 'SHORT_TERM' && months > 12 && (
              <div
                className="meta"
                style={{ marginTop: 6, fontSize: 12.5, color: 'var(--negative)', fontWeight: 600 }}
              >
                ⚠ อายุสัญญา {months} เดือน เกิน 12 เดือน — จะถูกตั้งสถานะ &quot;ทบทวน TFRS16&quot;
                เพื่อพิจารณาบันทึกเป็นสินทรัพย์
              </div>
            )}
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รายละเอียดบริการ
            </label>
            <textarea
              className="input"
              rows={2}
              placeholder="เช่น รปภ. 24 ชม. ประจำสาขา 200 แห่ง"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                fontSize: 14,
                minHeight: 60,
                resize: 'vertical',
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                แจ้งล่วงหน้าก่อนหมดอายุ (วัน)
              </label>
              <input
                className="input"
                type="number"
                value={noticePeriodDays}
                onChange={(e) => setNoticePeriodDays(parseInt(e.target.value, 10) || 0)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <label
              style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginTop: 26 }}
            >
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
              />{' '}
              ต่อสัญญาอัตโนมัติ
            </label>
          </div>

          <div
            style={{
              padding: '14px 16px',
              background: '#fafafa',
              borderRadius: 8,
              border: '1px solid var(--border)',
            }}
          >
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              การคำนวณ Recognition
            </div>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr 1.5fr',
                gap: 12,
                alignItems: 'flex-end',
              }}
            >
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  วันเริ่ม
                </label>
                <input
                  className="input"
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  จำนวนงวด
                </label>
                <select
                  className="select"
                  value={months}
                  onChange={(e) => setMonths(parseInt(e.target.value, 10))}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                >
                  <option value="6">6 เดือน</option>
                  <option value="12">12 เดือน</option>
                  <option value="24">24 เดือน</option>
                  <option value="36">36 เดือน</option>
                </select>
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  ค่าใช้จ่าย/เดือน
                </label>
                <input
                  className="input tnum"
                  type="number"
                  value={monthly}
                  onChange={(e) => setMonthly(parseFloat(e.target.value) || 0)}
                  style={{
                    width: '100%',
                    height: 38,
                    fontSize: 15,
                    textAlign: 'right',
                    fontWeight: 600,
                  }}
                />
              </div>
              <div>
                <label
                  style={{
                    fontSize: 13,
                    color: 'var(--text-muted)',
                    display: 'block',
                    marginBottom: 6,
                  }}
                >
                  มูลค่าสัญญารวม
                </label>
                <div
                  style={{
                    height: 38,
                    display: 'grid',
                    placeItems: 'center',
                    background: 'var(--brand)',
                    color: 'white',
                    borderRadius: 6,
                    fontSize: 16,
                    fontWeight: 700,
                    fontFamily: 'var(--font-display)',
                  }}
                  className="tnum"
                >
                  {fmt(monthly * months)}
                </div>
              </div>
            </div>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 13.5,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              <b>จะเกิดอะไรเมื่อบันทึก:</b> (1) สร้าง Schedule {months} งวด × {fmt(monthly)} (2)
              Auto-include ใน Monthly Run ทุกสิ้นเดือน (3) ลงบัญชี{' '}
              <b className="code">
                Dr. {expenseAccountCode || '—'} / Cr. 20170001 ค่าใช้จ่ายค้างจ่าย
              </b>{' '}
              (4) เตือนก่อนหมดอายุ 90 / 60 / 30 / 7 วัน
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={submit}
            disabled={createMut.isPending || !companyId || !expenseAccountCode}
          >
            <Icon name="check" size={14} />{' '}
            {createMut.isPending ? 'กำลังบันทึก...' : 'บันทึก & สร้าง Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RunModalProps {
  companyId: string | undefined;
  rows: readonly OCContract[];
  periods: readonly PeriodMaster[];
  onClose: () => void;
}

function RunModal({ companyId, rows, periods, onClose }: RunModalProps) {
  const items = rows.filter((c) => c.status === 'active' || c.status === 'expiring');
  const total = items.reduce((s, c) => s + c.monthly, 0);
  const open = openPeriods([...periods]);
  const [period, setPeriod] = useState<string>(() =>
    pickDefaultPeriod([...periods], new Date().toISOString().slice(0, 10)),
  );
  // Seed once periods arrive (they load async).
  const seeded = useRef(false);
  useEffect(() => {
    if (!seeded.current && open.length > 0 && !period) {
      setPeriod(pickDefaultPeriod([...periods], new Date().toISOString().slice(0, 10)));
      seeded.current = true;
    }
  }, [open.length, period, periods]);
  const accrueMut = useAccrueOpContracts(companyId);
  const noOpen = open.length === 0;
  const run = () => {
    if (!companyId || !period) return;
    accrueMut.mutate({ data: { period } }, { onSuccess: () => onClose() });
  };
  const label = period ? formatPeriodLabel(period, [...periods]) : '—';
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 920,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ส่งอนุมัติการรับรู้ — งวด {label}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              {items.length} สัญญา · รวม <b style={{ color: 'var(--brand)' }}>{fmt(total)}</b> บาท ·
              JV จะถูกโพสต์เมื่ออนุมัติ
            </div>
          </div>
          <select
            className="select"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            style={{ height: 34, marginLeft: 'auto', marginRight: 12 }}
          >
            {open.length === 0 ? (
              <option value="">— ไม่มีงวดเปิด —</option>
            ) : (
              open.map((p) => (
                <option key={p.period} value={p.period}>
                  {p.nameTh}
                </option>
              ))
            )}
          </select>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', overflow: 'auto', flex: 1 }}>
          {noOpen ? (
            <div
              className="muted"
              style={{
                marginBottom: 14,
                padding: '12px 14px',
                background: '#fbeae9',
                borderRadius: 6,
                fontSize: 14,
                color: 'var(--negative)',
              }}
            >
              ไม่มีงวดที่เปิดอยู่ — เปิดงวดบัญชีก่อนจึงจะประมวลผลได้
            </div>
          ) : null}
          <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden' }}>
            <table className="tbl">
              <thead>
                <tr>
                  <th style={{ width: 24 }}>
                    <input type="checkbox" defaultChecked />
                  </th>
                  <th>เลขที่</th>
                  <th>Vendor</th>
                  <th>ประเภท</th>
                  <th>เดบิต</th>
                  <th>เครดิต</th>
                  <th className="num">จำนวน</th>
                </tr>
              </thead>
              <tbody>
                {items.map((c) => {
                  const cat = getOCCat(c.cat);
                  return (
                    <tr key={c.id}>
                      <td>
                        <input type="checkbox" defaultChecked />
                      </td>
                      <td className="code">{c.id.slice(-6)}</td>
                      <td>
                        {c.vendor}
                        <div className="meta">{c.desc.slice(0, 40)}</div>
                      </td>
                      <td>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                          <span
                            style={{ width: 8, height: 8, borderRadius: 2, background: cat.tone }}
                          />
                          {cat.label}
                        </span>
                      </td>
                      <td className="code muted">{cat.acct}</td>
                      <td className="code muted">20170001</td>
                      <td className="num tnum">{fmt(c.monthly)}</td>
                    </tr>
                  );
                })}
                <tr className="total">
                  <td colSpan={6}>
                    รวมรับรู้ {items.length} สัญญา (Dr.ค่าใช้จ่าย / Cr.20170001 ค่าใช้จ่ายค้างจ่าย)
                  </td>
                  <td className="num tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(total)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            style={{
              marginTop: 14,
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              ขั้นตอน: (1) สร้างรายการรับรู้ (PENDING) + ส่งเข้าสายอนุมัติ — <b>ยังไม่โพสต์ JV</b>{' '}
              (2) ผู้มีอำนาจอนุมัติตามลำดับใน &quot;ประวัติการรับรู้รายเดือน&quot; (3)
              เมื่ออนุมัติขั้นสุดท้าย ระบบจึง Post JV รวม + เกิด Open Item ใน 20170001
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={run}
            disabled={accrueMut.isPending || !companyId || !period || noOpen}
          >
            <Icon name="check" size={14} />{' '}
            {accrueMut.isPending ? 'กำลังส่งอนุมัติ...' : 'ส่งอนุมัติการรับรู้'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface RenewModalProps {
  companyId: string | undefined;
  contract: OCContract;
  onClose: () => void;
}

export function RenewModal({ companyId, contract, onClose }: RenewModalProps) {
  // Renewal = create a linked successor contract (carrying forward the terms)
  // and terminate the predecessor — one atomic call to POST :id/renew.
  const [months, setMonths] = useState<number>(contract.months);
  const [monthly, setMonthly] = useState<number>(Math.round(contract.monthly * 1.03));
  const [startDate, setStartDate] = useState<string>(contract.end);
  const [endDate, setEndDate] = useState<string>(() => addMonthsLastDay(contract.end, months));
  const [noticePeriodDays, setNoticePeriodDays] = useState<number>(30);
  const [autoRenew, setAutoRenew] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>('');
  const renewMut = useRenewOpContract(companyId);
  const renew = () => {
    if (!companyId) return;
    // Only API-backed rows can be renewed; a row without a server-side apiId
    // (none today, since all rows come from the API) is skipped.
    const apiId = contract.apiId;
    if (!apiId) {
      onClose();
      return;
    }
    renewMut.mutate(
      {
        id: apiId,
        data: {
          startDate,
          endDate,
          monthlyAmount: monthly,
          noticePeriodDays,
          autoRenew,
          ...(notes.trim() ? { notes: notes.trim() } : {}),
        },
      },
      { onSuccess: () => onClose() },
    );
  };
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 760,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              ต่อสัญญา — {contract.vendor}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              สัญญาเดิม <b>{contract.id}</b> · หมดอายุ {fmtDate(contract.end)}
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ padding: '14px 16px', background: '#fafafa', borderRadius: 8 }}>
            <div className="section-title" style={{ margin: '0 0 10px' }}>
              เปรียบเทียบ — สัญญาเดิม vs ใหม่
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18 }}>
              <div>
                <div className="meta">สัญญาเดิม</div>
                <div style={{ fontSize: 14, marginTop: 6, lineHeight: 1.7 }}>
                  ระยะเวลา: <b>{contract.months} เดือน</b>
                  <br />
                  ค่าใช้จ่าย/เดือน: <b className="tnum">{fmt(contract.monthly)}</b>
                  <br />
                  รวมสัญญา: <b className="tnum">{fmt(contract.totalValue)}</b>
                </div>
              </div>
              <div>
                <div className="meta">สัญญาใหม่ (Renewal)</div>
                <div style={{ fontSize: 14, marginTop: 6, lineHeight: 1.7 }}>
                  ระยะเวลา:{' '}
                  <input
                    className="input"
                    type="number"
                    value={months}
                    onChange={(e) => {
                      const m = parseInt(e.target.value, 10) || 0;
                      setMonths(m);
                      setEndDate(addMonthsLastDay(startDate, m));
                    }}
                    style={{ width: 60, height: 26, fontSize: 14, padding: '0 6px' }}
                  />{' '}
                  เดือน
                  <br />
                  ค่าใช้จ่าย/เดือน:{' '}
                  <input
                    className="input tnum"
                    type="number"
                    value={monthly}
                    onChange={(e) => setMonthly(parseFloat(e.target.value) || 0)}
                    style={{
                      width: 120,
                      height: 26,
                      fontSize: 14,
                      padding: '0 6px',
                      textAlign: 'right',
                    }}
                  />
                  <br />
                  รวมสัญญา:{' '}
                  <b className="tnum" style={{ color: 'var(--brand)' }}>
                    {fmt(monthly * months)}
                  </b>{' '}
                  <span className="meta">(+3.0%)</span>
                </div>
              </div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันเริ่มสัญญาใหม่
              </label>
              <input
                className="input"
                type="date"
                value={startDate}
                onChange={(e) => {
                  setStartDate(e.target.value);
                  setEndDate(addMonthsLastDay(e.target.value, months));
                }}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                วันสิ้นสุด
              </label>
              <input
                className="input"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                แจ้งล่วงหน้าก่อนหมดอายุ (วัน)
              </label>
              <input
                className="input"
                type="number"
                value={noticePeriodDays}
                onChange={(e) => setNoticePeriodDays(parseInt(e.target.value, 10) || 0)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <label
              style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14, marginTop: 26 }}
            >
              <input
                type="checkbox"
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
              />{' '}
              ต่อสัญญาอัตโนมัติ
            </label>
          </div>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" defaultChecked /> Copy เงื่อนไขจากสัญญาเดิม (สาขา, Payment Terms,
            รายละเอียด)
          </label>
          <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
            <input type="checkbox" defaultChecked /> Generate Schedule
            อัตโนมัติต่อเนื่องจากสัญญาเดิม
          </label>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              หมายเหตุ Renewal
            </label>
            <textarea
              className="input"
              rows={2}
              placeholder="เช่น เจรจาเพิ่มราคา 3% ตามอัตราเงินเฟ้อ + บริการเพิ่มเติม..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              style={{
                width: '100%',
                padding: 10,
                fontSize: 14,
                minHeight: 60,
                resize: 'vertical',
              }}
            />
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            onClick={renew}
            disabled={renewMut.isPending || !companyId || !contract.apiId}
          >
            <Icon name="check" size={14} />{' '}
            {renewMut.isPending ? 'กำลังบันทึก...' : 'ต่อสัญญา & สร้าง Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
}
